/**
 * Phase River — unrolled execution graph visualization.
 *
 * Renders a horizontal pipeline: PM → [Loop N: Impl → Review → Consol] → Reflect → Integrate
 * The graph is data-driven from ExecutionGraphNode[] — no hardcoded stages.
 *
 * Two rendering modes:
 * - Full (Run Detail page): larger nodes with duration/turns
 * - Compact (Fleet card inline DAG): smaller nodes, minimal labels
 *
 * CSS: styles/components/phase-river.css, styles/views/fleet.css (DAG classes)
 */

import { esc } from '../../shared/rendering.js'
import { formatStageName, formatDuration } from '../../types/domain.js'
import { phaseIcon } from './icons.js'
import { action } from './action-delegation.js'
import type { ExecutionGraphNode } from '../../types/api.js'

// ─── Full Phase River (Run Detail) ──────────────────────────────────────────

/** Render the full-size phase river for the Run Detail page. */
export function renderPhaseRiver(nodes: readonly ExecutionGraphNode[]): string {
  if (nodes.length === 0) {
    return '<div class="pr"><span style="color:var(--t3);font-size:12px">No execution data</span></div>'
  }

  const groups = groupByLoopAndParallel(nodes)
  const parts: string[] = []

  for (const group of groups) {
    if (group.type === 'single') {
      if (parts.length > 0) {
        parts.push(connector(group.node.status === 'done' || group.node.status === 'running'))
      }
      parts.push(renderPhaseNode(group.node))
    } else if (group.type === 'parallel') {
      if (parts.length > 0) {
        parts.push(connector(group.nodes.every((n) => n.status === 'done')))
      }
      parts.push(renderParallelFork(group.nodes))
    } else if (group.type === 'loop') {
      if (parts.length > 0) {
        parts.push(connector(true))
      }
      parts.push(renderLoopBox(group.loopNumber, group.nodes))
    }
  }

  return `<div class="pr">${parts.join('')}</div>`
}

function renderPhaseNode(node: ExecutionGraphNode): string {
  const statusClass =
    node.status === 'done'
      ? 'done'
      : node.status === 'running'
        ? 'now'
        : node.status === 'failed'
          ? 'fail'
          : ''
  const icon = phaseIcon(node.status)
  const label = formatStageName(node.stage)
  const dur = formatDuration(node.duration_s)
  const turnsText = node.turns ? `${node.turns} turn${node.turns === 1 ? '' : 's'}` : ''
  const info = [dur, turnsText].filter(Boolean).join(' \u00b7 ')

  return `<div class="pn ${statusClass}" ${action('openDrawer', node.stage_id)} data-stage-id="${esc(node.stage_id)}">
    <div class="pi">${icon}</div>
    <span class="pl">${esc(label)}</span>
    ${info ? `<span class="pd">${info}</span>` : ''}
  </div>`
}

function renderParallelFork(nodes: readonly ExecutionGraphNode[]): string {
  const allDone = nodes.every((n) => n.status === 'done')
  const doneClass = allDone ? ' done' : ''
  const cols = nodes
    .map(
      (n) => `<div class="pr-fork-col">
      <div class="pr-fork-in${doneClass}"></div>
      ${renderPhaseNode(n)}
      <div class="pr-fork-out${doneClass}"></div>
    </div>`,
    )
    .join('')

  return `<div class="pr-fork">
    <div class="pr-fork-bracket left${doneClass}"></div>
    ${cols}
    <div class="pr-fork-bracket right${doneClass}"></div>
  </div>`
}

function renderLoopBox(loopNumber: number, nodes: readonly ExecutionGraphNode[]): string {
  const hasFailure = nodes.some((n) => n.status === 'failed')
  const isRunning = nodes.some((n) => n.status === 'running')
  const bg = hasFailure
    ? 'rgba(248,113,113,.06)'
    : isRunning
      ? 'rgba(56,189,248,.06)'
      : 'rgba(62,207,160,.06)'
  const bd = hasFailure
    ? 'rgba(248,113,113,.08)'
    : isRunning
      ? 'rgba(56,189,248,.1)'
      : 'rgba(62,207,160,.08)'

  const innerGroups = groupByParallel(nodes)
  const innerParts: string[] = []

  for (const g of innerGroups) {
    if (innerParts.length > 0) {
      innerParts.push(
        miniEdge(
          g.type === 'single'
            ? g.node.status === 'done'
            : g.nodes.every((n) => n.status === 'done'),
        ),
      )
    }
    if (g.type === 'single') {
      innerParts.push(renderMiniNode(g.node))
    } else {
      innerParts.push(renderMiniParallel(g.nodes))
    }
  }

  return `<div style="background:${bg};border:1px solid ${bd};border-radius:5px;padding:3px 2px;display:flex;align-items:center;flex-shrink:0" data-loop="${loopNumber}">
    ${innerParts.join('')}
  </div>`
}

function renderMiniNode(node: ExecutionGraphNode): string {
  const statusClass = node.status === 'done' ? 'completed' : node.status
  const icon = phaseIcon(node.status)
  const label = formatStageName(node.stage)
  return `<div class="dag-node ${statusClass}" ${action('openDrawer', node.stage_id)} style="min-width:30px">
    <div class="dag-icon" style="width:16px;height:16px;font-size:7px;border-radius:4px">${icon}</div>
    <div class="dag-label" style="font-size:6px">${esc(label)}</div>
  </div>`
}

function renderMiniParallel(nodes: readonly ExecutionGraphNode[]): string {
  const cols = nodes.map((n) => renderMiniNode(n)).join('')
  return `<div style="display:flex;flex-direction:column;gap:1px">${cols}</div>`
}

function connector(done: boolean): string {
  return `<div class="pc${done ? ' done' : ''}"></div>`
}

function miniEdge(done: boolean): string {
  return `<div style="width:4px;height:2px;background:${done ? 'var(--grn)' : 'var(--s4)'};${done ? 'opacity:.4;' : ''}flex-shrink:0"></div>`
}

// ─── Compact DAG (Fleet Card) ───────────���───────────────────────────────────

/** Render a compact inline DAG for a fleet run card. */
export function renderCompactDag(nodes: readonly ExecutionGraphNode[]): string {
  if (nodes.length === 0) {
    return ''
  }

  const groups = groupByLoopAndParallel(nodes)
  const parts: string[] = []

  for (const group of groups) {
    if (parts.length > 0) {
      const prevDone =
        group.type === 'single'
          ? group.node.status === 'done'
          : group.type === 'loop'
            ? group.nodes.every((n) => n.status === 'done')
            : group.nodes.every((n) => n.status === 'done')
      parts.push(`<div class="dag-edge${prevDone ? ' done' : ''}" style="min-width:4px"></div>`)
    }
    if (group.type === 'single') {
      parts.push(renderDagNode(group.node, 20))
    } else if (group.type === 'loop') {
      parts.push(renderLoopBox(group.loopNumber, group.nodes))
    } else {
      // parallel group at top level
      const cols = group.nodes.map((n) => renderDagNode(n, 16)).join('')
      parts.push(`<div style="display:flex;flex-direction:column;gap:2px">${cols}</div>`)
    }
  }

  return `<div class="dag">${parts.join('')}</div>`
}

function renderDagNode(node: ExecutionGraphNode, size = 20): string {
  const statusClass = node.status === 'done' ? 'completed' : node.status
  const icon = phaseIcon(node.status)
  const label = formatStageName(node.stage)
  const r = Math.round(size * 0.25)
  const fs = Math.round(size * 0.44)
  return `<div class="dag-node ${statusClass}" style="min-width:${size + 14}px">
    <div class="dag-icon" style="width:${size}px;height:${size}px;font-size:${fs}px;border-radius:${r}px">${icon}</div>
    <div class="dag-label" style="font-size:${Math.max(5, fs - 1)}px">${esc(label)}</div>
  </div>`
}

// ─── Graph Grouping Logic ───────────────────────────────────────────────────

type NodeGroup =
  | { type: 'single'; node: ExecutionGraphNode }
  | { type: 'parallel'; nodes: ExecutionGraphNode[] }
  | { type: 'loop'; loopNumber: number; nodes: ExecutionGraphNode[] }

type InnerGroup =
  | { type: 'single'; node: ExecutionGraphNode }
  | { type: 'parallel'; nodes: ExecutionGraphNode[] }

/** Group nodes into sequential singles, parallel groups, and loop boxes. */
function groupByLoopAndParallel(nodes: readonly ExecutionGraphNode[]): NodeGroup[] {
  const groups: NodeGroup[] = []
  const loopMap = new Map<number, ExecutionGraphNode[]>()
  const singles: ExecutionGraphNode[] = []

  // Separate loop nodes from non-loop nodes
  for (const node of nodes) {
    if (node.loop_number !== null) {
      const existing = loopMap.get(node.loop_number) ?? []
      existing.push(node)
      loopMap.set(node.loop_number, existing)
    } else {
      singles.push(node)
    }
  }

  // Build output: PM first, then loops in order, then post-loop stages
  const preLoop = singles.filter((n) => isPre(n.stage))
  const postLoop = singles.filter((n) => !isPre(n.stage))

  for (const node of preLoop) {
    groups.push({ type: 'single', node })
  }

  const sortedLoops = [...loopMap.entries()].sort((a, b) => a[0] - b[0])
  for (const [loopNum, loopNodes] of sortedLoops) {
    groups.push({ type: 'loop', loopNumber: loopNum, nodes: loopNodes })
  }

  for (const node of postLoop) {
    groups.push({ type: 'single', node })
  }

  return groups
}

/** Group nodes within a loop into singles and parallel groups. */
function groupByParallel(nodes: readonly ExecutionGraphNode[]): InnerGroup[] {
  const groups: InnerGroup[] = []
  const parallelMap = new Map<string, ExecutionGraphNode[]>()
  const ordered: { key: string; isParallel: boolean }[] = []

  for (const node of nodes) {
    if (node.parallel_group) {
      const existing = parallelMap.get(node.parallel_group) ?? []
      if (existing.length === 0) {
        ordered.push({ key: node.parallel_group, isParallel: true })
      }
      existing.push(node)
      parallelMap.set(node.parallel_group, existing)
    } else {
      ordered.push({ key: node.stage_id, isParallel: false })
    }
  }

  const seen = new Set<string>()
  for (const entry of ordered) {
    if (seen.has(entry.key)) {
      continue
    }
    seen.add(entry.key)
    if (entry.isParallel) {
      const pNodes = parallelMap.get(entry.key) ?? []
      groups.push({ type: 'parallel', nodes: pNodes })
    } else {
      const node = nodes.find((n) => n.stage_id === entry.key)
      if (node) {
        groups.push({ type: 'single', node })
      }
    }
  }

  return groups
}

/** Pre-loop stages. Post-loop stages are everything else with loop_number === null. */
function isPre(stage: string): boolean {
  return stage === 'product-manager'
}
